(function () {
  const normalizeImagePath = (value) => {
    if (!value) return '';

    try {
      return new URL(value, document.baseURI).pathname;
    } catch (error) {
      return value;
    }
  };

  class ProductCardGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.galleryInitialized === 'true') return;

      this.dataset.galleryInitialized = 'true';
      this.track = this.querySelector('[data-product-card-gallery-track]');
      this.link = this.querySelector('[data-product-card-gallery-link]');
      this.previousButton = this.querySelector('[data-product-card-gallery-previous]');
      this.nextButton = this.querySelector('[data-product-card-gallery-next]');
      this.activeIndex = 0;
      this.dragged = false;
      this.pointerStart = null;
      this.scrollStart = 0;
      this.scrollFrame = null;
      this.scrollEndTimer = null;
      this.imagesPreloaded = false;
      this.refreshSlides();

      if (!this.track || this.slides.length < 2) {
        if (this.previousButton) this.previousButton.hidden = true;
        if (this.nextButton) this.nextButton.hidden = true;
        return;
      }

      this.createLoopClones();

      this.previousButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(this.activeIndex - 1);
      });

      this.nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.goTo(this.activeIndex + 1);
      });

      this.track.addEventListener('pointerdown', (event) => {
        this.dragged = false;
        this.pointerStart = { x: event.clientX, y: event.clientY };
        this.scrollStart = this.track.scrollLeft;
        this.classList.add('product-card-gallery--interacting');
        this.preloadAround(this.activeIndex);
      });

      this.track.addEventListener('pointermove', (event) => {
        if (!this.pointerStart) return;

        const distanceX = Math.abs(event.clientX - this.pointerStart.x);
        const distanceY = Math.abs(event.clientY - this.pointerStart.y);
        if (distanceX > 8 && distanceX > distanceY) this.dragged = true;
      });

      this.track.addEventListener('pointerup', () => {
        this.pointerStart = null;
        if (!this.dragged) {
          this.classList.remove('product-card-gallery--interacting');
          return;
        }

        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = setTimeout(() => this.normalizeLoopPosition(), 100);
      });

      this.track.addEventListener('pointercancel', () => {
        this.pointerStart = null;
        clearTimeout(this.scrollEndTimer);
        this.scrollEndTimer = setTimeout(() => this.normalizeLoopPosition(), 100);
      });

      this.track.addEventListener(
        'scroll',
        () => {
          if (this.pointerStart && Math.abs(this.track.scrollLeft - this.scrollStart) > 4) {
            this.dragged = true;
          }
          if (this.dragged) this.classList.add('product-card-gallery--interacting');

          if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = requestAnimationFrame(() => this.handleScroll());
          clearTimeout(this.scrollEndTimer);
          this.scrollEndTimer = setTimeout(() => this.normalizeLoopPosition(), 100);
        },
        { passive: true },
      );

      this.track.addEventListener('scrollend', () => this.normalizeLoopPosition());

      this.link?.addEventListener(
        'click',
        (event) => {
          if (!this.dragged) return;

          event.preventDefault();
          event.stopPropagation();
          this.dragged = false;
        },
        true,
      );

      if ('IntersectionObserver' in window) {
        this.preloadObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;

            this.preloadAround(this.activeIndex);
            this.preloadObserver.disconnect();
          },
          { rootMargin: '200px' },
        );
        this.preloadObserver.observe(this);
      } else {
        this.preloadAround(this.activeIndex);
      }
    }

    disconnectedCallback() {
      this.preloadObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      if (this.initialPositionFrame) cancelAnimationFrame(this.initialPositionFrame);
      clearTimeout(this.scrollEndTimer);
    }

    refreshSlides() {
      if (!this.track) {
        this.slides = [];
        return;
      }

      this.slides = Array.from(this.track.children).filter(
        (slide) =>
          slide.matches('[data-product-card-gallery-slide]') &&
          !slide.hasAttribute('data-product-card-gallery-clone'),
      );
    }

    wrapIndex(index) {
      if (!this.slides.length) return 0;
      return ((index % this.slides.length) + this.slides.length) % this.slides.length;
    }

    updateActiveSlide() {
      this.slides.forEach((slide, index) => {
        slide.classList.toggle('product-card-gallery__slide--active', index === this.activeIndex);
      });
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.productCardGalleryClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.classList.remove('product-card-gallery__slide--active');
      clone.querySelectorAll('[id]').forEach((element) => element.removeAttribute('id'));
      clone.querySelectorAll('img').forEach((image) => {
        image.alt = '';
        image.classList.remove('media--first');
      });
      return clone;
    }

    removeLoopClones() {
      this.track
        ?.querySelectorAll('[data-product-card-gallery-clone]')
        .forEach((clone) => clone.remove());
    }

    createLoopClones() {
      this.removeLoopClones();
      this.refreshSlides();
      if (this.slides.length < 2) return;

      this.track.prepend(this.createClone(this.slides[this.slides.length - 1]));
      this.track.append(this.createClone(this.slides[0]));
      this.reset();

      if (!this.track.clientWidth) {
        this.initialPositionFrame = requestAnimationFrame(() => this.reset());
      }
    }

    preloadAround(index) {
      this.imagesPreloaded = true;
      const indexes = [index, index + 1, index - 1];

      new Set(indexes.map((slideIndex) => this.wrapIndex(slideIndex))).forEach((slideIndex) => {
        const slide = this.slides[slideIndex];
        const image = slide.querySelector('img');
        if (image) image.loading = 'eager';
      });
    }

    jumpToPhysicalIndex(index) {
      if (!this.track?.clientWidth) return;

      const targetSlide = this.track.children[index];
      if (!targetSlide) return;

      const trackBounds = this.track.getBoundingClientRect();
      const targetBounds = targetSlide.getBoundingClientRect();
      const left = this.track.scrollLeft + targetBounds.left - trackBounds.left;
      if (typeof this.track.scrollTo === 'function') {
        this.track.scrollTo({ left, behavior: 'auto' });
      } else {
        this.track.scrollLeft = left;
      }
    }

    goTo(index) {
      if (!this.track || !this.slides.length) return;

      this.activeIndex = this.wrapIndex(index);
      this.updateActiveSlide();
      this.preloadAround(this.activeIndex);
      this.jumpToPhysicalIndex(this.activeIndex + 1);
    }

    handleScroll() {
      if (!this.track.clientWidth || !this.slides.length) return;

      const physicalIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (physicalIndex <= 0) {
        this.activeIndex = this.slides.length - 1;
      } else if (physicalIndex >= this.slides.length + 1) {
        this.activeIndex = 0;
      } else {
        this.activeIndex = physicalIndex - 1;
      }
      this.updateActiveSlide();
      this.preloadAround(this.activeIndex);
    }

    normalizeLoopPosition() {
      if (!this.track?.clientWidth || !this.slides.length) return;

      const physicalIndex = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (physicalIndex <= 0) {
        this.activeIndex = this.slides.length - 1;
        this.jumpToPhysicalIndex(this.slides.length);
      } else if (physicalIndex >= this.slides.length + 1) {
        this.activeIndex = 0;
        this.jumpToPhysicalIndex(1);
      } else {
        this.activeIndex = physicalIndex - 1;
      }
      this.updateActiveSlide();
      this.preloadAround(this.activeIndex);
      this.classList.remove('product-card-gallery--interacting');
    }

    reset() {
      this.activeIndex = 0;
      this.updateActiveSlide();
      this.jumpToPhysicalIndex(1);
    }

    setPrimaryImage(newPrimaryImage) {
      if (!newPrimaryImage || !this.track) return;

      this.removeLoopClones();
      this.refreshSlides();
      const firstSlide = this.slides[0];
      if (!firstSlide) return;

      const mediaId = newPrimaryImage.dataset.mediaId || '';
      const imagePath = normalizeImagePath(newPrimaryImage.dataset.imagePath || newPrimaryImage.src);
      const matchingSlide = this.slides.find((slide) => {
        if (mediaId && slide.dataset.mediaId === mediaId) return true;
        return normalizeImagePath(slide.dataset.imagePath) === imagePath;
      });

      if (matchingSlide) {
        if (matchingSlide !== firstSlide) this.track.prepend(matchingSlide);
      } else {
        const currentPrimaryImage = firstSlide.querySelector('img');
        currentPrimaryImage?.replaceWith(newPrimaryImage);
        firstSlide.dataset.mediaId = mediaId;
        firstSlide.dataset.imagePath = newPrimaryImage.dataset.imagePath || newPrimaryImage.src;
      }

      this.refreshSlides();
      this.slides.forEach((slide, index) => {
        const image = slide.querySelector('img');
        if (!image) return;

        image.classList.toggle('media--first', index === 0);
        image.classList.add('product-card-gallery__image');
      });

      this.createLoopClones();
      if (this.imagesPreloaded) this.preloadAround(this.activeIndex);
    }
  }

  if (!customElements.get('product-card-gallery')) {
    customElements.define('product-card-gallery', ProductCardGallery);
  }
})();
